// FILECONTENT_START src/common/components/Inputs/Signature/Signature.tsx
import ClearIcon from "@mui/icons-material/Clear";
import {
  alpha,
  Box,
  Button,
  FormHelperText,
  IconButton,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef } from "react";
//@ts-ignore
import tgrx from "@tgrx/getcontrasttext";

import ReactSignatureCanvas from "react-signature-canvas";
import stc from "string-to-color";

export interface ColorIndicatorProps {
  /**
   * Show vertical line at left of the field inside. color string or "auto" generate unique color by props.id
   */
  indicatorColor?: "auto" | string;
  /**
   * It works only with indicatorColor="auto"
   */
  indicatorOpacity?: number;
}

export function getContrastColor(
  color?: string,
  opacity: number = 1,
  contrastThreshold: number = 3
): string {
  if (!color) {
    return "#000000";
  }

  return tgrx.default({
    background: color,
    contrastThreshold: contrastThreshold / opacity,
    warnings: false,
  });
}

export function getColorsFromString(
  str?: string,
  opacity?: number
): {
  color?: string;
  backgroundColor?: string;
} {
  if (!str) {
    return {};
  }
  let backgroundColor = opacity ? alpha(stc(str), opacity) : stc(str);
  const color = getContrastColor(backgroundColor, opacity);
  return { color, backgroundColor };
}

export function getTextFieldColorIndicatorSx(
  id: any,
  { indicatorColor, indicatorOpacity }: ColorIndicatorProps
) {
  let sx = {};
  if (indicatorColor === "auto") {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${
          getColorsFromString(id, indicatorOpacity || 1).backgroundColor
        }`,
      },
    };
  } else if (indicatorColor) {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${indicatorColor}`,
      },
    };
  }
  return sx;
}

export type SignatureState = {
  hour: string;
  minute: string;
  ampm: string;
  open: boolean;
  value: string;
};

export type SignatureProps = {
  clearable?: boolean;
  required?: boolean;
  isDirty?: boolean;
  is24Hours?: boolean;
  minuteIncrement?: number;
  shorcutIncrement?: number;
  changeOnAccept?: boolean;
  //Depricated
  minutesStep?: boolean;
  width?: number;
  height?: number;
  penColor?: string;
  clearLabel?: string;
} & ColorIndicatorProps &
  TextFieldProps;

type CanvasPoint = [number, number];
type Point = {
  x: number;
  y: number;
  //time: number;
  //color?: string | undefined;
};

export default function Signature({
  id,
  error,
  helperText,
  clearable = true,
  indicatorColor,
  indicatorOpacity,
  value,
  onChange,
  isDirty,
  onBlur,
  label,
  minuteIncrement = 1,
  shorcutIncrement = 15,
  changeOnAccept = true,
  required,
  penColor = "black",
  //Depricated
  minutesStep,
  width = 500,
  height = 200,
  sx,
  clearLabel,
  ...rest
}: SignatureProps) {
  const canvasRef = useRef<ReactSignatureCanvas>();
  const theme = useTheme();

  function handleOnEnd(evt: any) {
    if (canvasRef.current) {
      onChange &&
        onChange({
          target: {
            value: canvasRef.current.toDataURL("png", {
              ratio: window.devicePixelRatio || 1,
            }),
          },
        } as any);
    }
  }

  function handleClear(evt: any) {
    if (canvasRef.current) {
      canvasRef.current.clear();
      onChange && onChange({ target: { value: null } } as any);
    }
  }

  /*const inputRef = useRef();*/

  const _label = required ? (
    <>
      {label} <b style={{ color: "red" }}> *</b>
    </>
  ) : (
    label
  );

  function resizeCanvas() {}
  function resizeCanvas2() {
    var ratio = Math.max(window.devicePixelRatio || 1, 1);
    if (canvasRef.current) {
      const canvas = canvasRef.current.getCanvas();
      if (canvas) {
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        //@ts-ignore
        canvas.getContext("2d").scale(ratio, ratio);
      }
    }
  }

  useEffect(() => {
    if (canvasRef.current) {
      if (value) {
        resizeCanvas();
        canvasRef.current.fromDataURL(value as any, {
          width,
          height,
          ratio: window.devicePixelRatio || 1,
        });
      } else {
        resizeCanvas();
        canvasRef.current.clear();
      }
    }
  }, [value]);
  useEffect(() => {
    if (canvasRef.current) {
      resizeCanvas();
    }
  }, []);

  /*useEffect(() => {
    if (canvasRef.current) {
      setTimeout(() => {
        resizeCanvas();
      }, 2000);
    }
  }, []);*/

  return (
    <Box
      sx={{
        border: `solid 1px ${theme.palette.divider}`,
        borderRadius: "4px",
        overflow: "hidden",
        width: `${width}px`,
        paddingBottom: "6px",
        ...sx,
      }}
    >
      <ReactSignatureCanvas
        ref={canvasRef as any}
        penColor={penColor}
        canvasProps={{
          width,
          height,
          style: {
            //width: "100%",
            //width: `${width * (window.devicePixelRatio | 1)}px`,
            //height: `${height * (window.devicePixelRatio | 1)}px`,
            backgroundColor:
              theme.palette.mode === "dark" ? alpha("#FFFFFF", 0.4) : undefined,
            borderBottom: `solid 1px ${theme.palette.divider}`,
          },
        }}
        clearOnResize
        onEnd={handleOnEnd}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          paddingRight: "6px",
          paddingLeft: "12px",
        }}
      >
        <Typography>{_label}</Typography>
        {clearable && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {!!clearLabel ? (
              <Button
                variant="text"
                endIcon={<ClearIcon />}
                onClick={handleClear}
                disabled={!value}
              >
                {clearLabel}
              </Button>
            ) : (
              <IconButton onClick={handleClear} disabled={!value}>
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      {(error || helperText) && (
        <FormHelperText
          sx={{
            paddingRight: "12px",
            paddingLeft: "12px",
          }}
          error={!!error}
        >
          {error || helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

// FILECONTENT_END src/common/components/Inputs/Signature/Signature.tsx